import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SlidersHorizontal, ChevronDown, Check } from 'lucide-react';
import ProductCard from './ProductCard';
import { filterProducts, sortProducts, getAvailableFilters, shuffleProducts } from '../utils/productLogic';
import { SortOption, ProductFilterState, Product } from '../types';
import { db } from '../utils/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { getWindowOpenAt, getWindowState } from '../utils/productWindows';

interface ProductsPageProps {
  title: string;
  subtitle?: string;
  heroImage: string;
  onProductClick?: (product: Product) => void;
  mode?: 'now' | 'soon' | 'catalog';
  categoryFilter?: string[];
  tierFilter?: Product['tier']; // Filtered view by product tier (uniform / artifact)
  pageId?: string; // ID for dynamic content (e.g. 'shop', 'uniform')
}

const ProductsPage: React.FC<ProductsPageProps> = ({ title: defaultTitle, subtitle: defaultSubtitle, heroImage: defaultHeroImage, onProductClick, mode = 'catalog', categoryFilter, tierFilter, pageId }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('featured');
  const [allPageProducts, setAllPageProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dynamic Page Content State
  const [pageContent, setPageContent] = useState({
    title: defaultTitle,
    subtitle: defaultSubtitle,
    heroImage: defaultHeroImage
  });

  // Fetch Dynamic Page Content (with race condition protection)
  useEffect(() => {
    let isMounted = true;

    // Reset to defaults immediately when props change
    setPageContent({
        title: defaultTitle,
        subtitle: defaultSubtitle,
        heroImage: defaultHeroImage
    });

    const fetchPageContent = async () => {
        if (!pageId) return;
        try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('../utils/firebase');
            const docRef = doc(db, 'page_content', pageId);
            const docSnap = await getDoc(docRef);

            if (isMounted && docSnap.exists()) {
                const data = docSnap.data();
                setPageContent(prev => ({
                    title: data.title || prev.title,
                    subtitle: data.subtitle || prev.subtitle,
                    heroImage: data.heroImage || prev.heroImage
                }));
            }
        } catch (error: any) {
            if (error.code === 'permission-denied') {
                console.warn("Dynamic Page Headers: Permission denied. Ensure Firestore Rules are deployed.");
            } else {
                console.error("Error fetching page content:", error);
            }
        }
    };

    fetchPageContent();

    return () => { isMounted = false; };
  }, [pageId, defaultTitle, defaultSubtitle, defaultHeroImage]);
  
  // Filter State
  const [filters, setFilters] = useState<ProductFilterState>({
    category: [],
    ridingStyle: [],
    priceRange: [0, 500],
  });

  // Parse URL Search Params for initial filters
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    const sortParam = params.get('sort');

    if (categoryParam) {
      setFilters(prev => ({ ...prev, category: [categoryParam] }));
    }
    
    if (sortParam) {
       setSortOption(sortParam as SortOption); 
       if (sortParam === 'new') setSortOption('newest');
       else setSortOption(sortParam as SortOption);
    }
  }, [location.search]);

  // Fetch dynamic products from Firestore
  useEffect(() => {
    const fetchDynamicProducts = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const fetchedProducts: Product[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out Vault/Sold Out items so they don't appear in the standard Shop
          if (data.status === 'sold_out' || data.category === 'Vault') return;

          // Apply prop-based Category Filter if present
          if (categoryFilter && categoryFilter.length > 0) {
            if (!categoryFilter.includes(data.category)) return;
          }

          // Apply Tier Filter if present
          if (tierFilter) {
              if (data.tier !== tierFilter) return;
          }

          fetchedProducts.push({ id: doc.id, ...data } as Product);
        });

        // Randomise the main shop's default presentation once per visit.
        // Explicit sort options (price, newest) still reorder on top.
        setAllPageProducts(pageId === 'shop' ? shuffleProducts(fetchedProducts) : fetchedProducts);
      } catch (error) {
        console.error("Error fetching dynamic products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDynamicProducts();
  }, [categoryFilter, tierFilter, pageId]);

  // Surfacing Soon subline: "The next fragment opens {date}. Seven days. Then closed."
  const soonSubtitle = useMemo(() => {
    if (mode !== 'soon') return null;
    const now = new Date();
    const upcoming = allPageProducts
      .map(p => getWindowOpenAt(p))
      .filter((d): d is string => !!d && new Date(d) > now)
      .sort();
    if (upcoming.length === 0) return null;
    const nextOpen = new Date(upcoming[0]).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    return `The next fragment opens ${nextOpen}. Seven days. Then closed.`;
  }, [mode, allPageProducts]);

  // Derived Logic (Memoized for performance)
  const availableFilters = useMemo(() => getAvailableFilters(allPageProducts), [allPageProducts]);
  
  const processedProducts = useMemo(() => {
    // 1. Filter by Mode ('now' vs 'soon') using window mechanics
    let baseList = allPageProducts;
    const now = new Date();

    // Closed-window fragments live in the Vault; withdrawn artifacts have
    // returned to the archive — neither belongs in the shop grids
    baseList = baseList.filter(p => getWindowState(p, now) !== 'closed' && !p.withdrawn);

    if (mode === 'now') {
      baseList = baseList.filter(p => getWindowState(p, now) === 'open');
    } else if (mode === 'soon') {
      const soon = (p: Product) => {
        const openAt = getWindowOpenAt(p);
        return !!openAt && new Date(openAt) > now;
      };
      baseList = baseList.filter(soon);
    }

    // 2. Standard Filters
    const filtered = filterProducts(baseList, filters);
    
    // 3. Sort
    return sortProducts(filtered, sortOption);
  }, [allPageProducts, filters, sortOption, mode]);

  const toggleFilter = (type: 'category' | 'ridingStyle', value: string) => {
    setFilters(prev => {
      const current = prev[type];
      const next = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [type]: next };
    });
  };

  // Image Source State (to handle fallbacks reliably)
  const [imgSrc, setImgSrc] = useState(defaultHeroImage);
  const [hasError, setHasError] = useState(false);

  // Sync image source when content changes
  useEffect(() => {
    // console.log("ProductsPage: Updating imgSrc", { dynamic: pageContent.heroImage, default: defaultHeroImage });
    setImgSrc(pageContent.heroImage || defaultHeroImage);
    setHasError(false);
  }, [pageContent.heroImage, defaultHeroImage]);

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Header */}
      <div className="relative h-[200px] md:h-[250px] lg:h-[300px] bg-gray-900 overflow-hidden">
        {!hasError ? (
            <img
            src={imgSrc}
            alt={pageContent.title}
            onError={() => {
                console.warn("ProductsPage: Image load failed", imgSrc);
                if (imgSrc !== defaultHeroImage) {
                   setImgSrc(defaultHeroImage);
                } else {
                   setHasError(true);
                }
            }}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
        ) : (
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-800 to-black opacity-60" />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-normal tracking-widest uppercase mb-4">{pageContent.title}</h1>
          {(soonSubtitle || pageContent.subtitle) && (
            <p className="font-heading text-base md:text-lg tracking-widest">{soonSubtitle || pageContent.subtitle}</p>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        
        {loading ? (
           <div className="min-h-[40vh] flex items-center justify-center">
             <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
           </div>
        ) : (
          <>
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b border-gray-100 pb-4 gap-4">
          <button 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-2 font-heading font-bold text-sm tracking-widest hover:text-brand-gray transition-colors"
          >
            <SlidersHorizontal size={18} />
            {isFilterOpen ? 'HIDE FILTERS' : 'SHOW FILTERS'}
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-body">{processedProducts.length} PRODUCTS</span>
            <div className="relative group">
              <button className="flex items-center gap-1 font-heading font-bold text-sm tracking-widest ml-4">
                SORT BY <ChevronDown size={14} />
              </button>
              <div className="absolute right-0 top-full mt-2 w-48 bg-white shadow-xl z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                {[
                  { label: 'FEATURED', value: 'featured' },
                  { label: 'PRICE: LOW TO HIGH', value: 'price-asc' },
                  { label: 'PRICE: HIGH TO LOW', value: 'price-desc' },
                  { label: 'NEWEST', value: 'newest' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSortOption(opt.value as SortOption)}
                    className={`block w-full text-left px-4 py-3 text-sm font-body hover:bg-gray-50 ${sortOption === opt.value ? 'font-bold text-brand-gold' : 'text-gray-600'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          
          {/* Sidebar Filters */}
          <div className={`lg:col-span-3 space-y-8 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
            
            {/* Categories */}
            {availableFilters.categories.length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-lg tracking-widest mb-4 uppercase">Category</h3>
                <ul className="space-y-2">
                  {availableFilters.categories.map(cat => (
                    <li key={cat}>
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-4 h-4 border border-gray-300 flex items-center justify-center transition-colors ${filters.category.includes(cat) ? 'bg-black border-black' : 'group-hover:border-black'}`}>
                           {filters.category.includes(cat) && <Check size={10} className="text-white" />}
                        </div>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          onChange={() => toggleFilter('category', cat)}
                          checked={filters.category.includes(cat)}
                        />
                        <span className="font-heading text-sm text-gray-600 group-hover:text-black transition-colors tracking-wide">{cat}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Riding Style (Only if available) */}
            {availableFilters.ridingStyles.length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-lg tracking-widest mb-4 uppercase">Riding Style</h3>
                <ul className="space-y-2">
                  {availableFilters.ridingStyles.map(style => (
                    <li key={style}>
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-4 h-4 border border-gray-300 flex items-center justify-center transition-colors ${filters.ridingStyle.includes(style) ? 'bg-black border-black' : 'group-hover:border-black'}`}>
                           {filters.ridingStyle.includes(style) && <Check size={10} className="text-white" />}
                        </div>
                        <input 
                          type="checkbox" 
                          className="hidden" 
                          onChange={() => toggleFilter('ridingStyle', style)}
                          checked={filters.ridingStyle.includes(style)}
                        />
                        <span className="font-heading text-sm text-gray-600 group-hover:text-black transition-colors tracking-wide">{style}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Price (Simple representation) */}
             <div>
              <h3 className="font-heading font-bold text-lg tracking-widest mb-4 uppercase">Price Range</h3>
              <div className="font-body text-sm text-gray-600">
                £{availableFilters.minPrice} - £{availableFilters.maxPrice}
              </div>
            </div>

          </div>

          {/* Product Grid */}
          <div className="lg:col-span-9">
            {processedProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-12">
                {processedProducts.map(product => (
                  <div key={product.id} className="lg:col-span-4">
                    <ProductCard product={product} onClick={onProductClick} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <p className="font-heading text-xl text-gray-400">No products match your selection.</p>
                <button 
                  onClick={() => setFilters({ category: [], ridingStyle: [], priceRange: [0, 500] })}
                  className="mt-4 text-sm underline hover:text-brand-gold"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>

        </div>
         </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
